import { useCallback } from 'react';
import { useAtom } from 'jotai';
import useAPI from '../../../api';
import { layoutEditAtom } from '../../../Atoms';
import { DETAIL_COLUMNS } from '../detail/resolveDetail';
import {
  pickSavedLayout,
  parseSavedLayout,
} from '../../../Hooks/useSectionLayout';
import useLayoutWrites from './useLayoutWrites';

/** The picker entry that starts a layout from the checked-in default rather than loading one. */
export const NEW_LAYOUT = 0;

/** A region dropped onto the canvas, before it is moved or sized. */
const NEW_REGION = { x: 0, y: 0, w: 4, h: 6 };

/** Whether two placements share a cell. */
const collides = (one, other) =>
  one.x < other.x + other.w &&
  one.x + one.w > other.x &&
  one.y < other.y + other.h &&
  one.y + one.h > other.y;

/**
 * The first row at or below the one asked for where a placement stands clear of everything else.
 *
 * A region arrives clear of its neighbours because the grid resolves an overlap by moving both
 * items - the one being placed down by its own height, the one already there up into the row it
 * left. Interactively that settles, since it happens once inside the grid's own state; arriving
 * through the layout it does not, because each pass is a real change that comes back as another
 * layout to resolve.
 */
export const freeRow = (regions, placement) => {
  const placed = regions.map(({ x = 0, y = 0, w = 4, h = 6 }) => ({
    x,
    y,
    w,
    h,
  }));
  let { y } = placement;

  while (placed.some((region) => collides({ ...placement, y }, region))) y += 1;

  return y;
};

/**
 * The regions at a level of the tree, named by the ids leading down to it.
 *
 * @param {Array} regions - The page's regions.
 * @param {Array} path - The ids of the containers opened, outermost first.
 *
 * @returns {Array}
 */
const regionsAt = (regions, path) =>
  (path ?? []).reduce(
    (held, id) => held.find((region) => region.id === id)?.regions ?? [],
    regions ?? []
  );

/**
 * The regions along a path, outermost first: what the sidebar has opened its way through.
 *
 * @param {Array} regions - The page's regions.
 * @param {Array} path - The ids of the containers opened, outermost first.
 *
 * @returns {Array}
 */
const trailTo = (regions, path) =>
  (path ?? []).reduce(
    ({ held, walked }, id) => {
      const region = held.find((one) => one.id === id) ?? null;

      return {
        held: region?.regions ?? [],
        walked: region ? [...walked, region] : walked,
      };
    },
    { held: regions ?? [], walked: [] }
  ).walked;

/**
 * The tree with the regions at a level replaced, leaving every other branch as it was.
 *
 * @param {Array} regions - The regions of the level being walked into.
 * @param {Array} path - What is left of the path to the level being written.
 * @param {Array} next - The regions to put there.
 *
 * @returns {Array}
 */
const writeAt = (regions, path, next) => {
  if ((path ?? []).length === 0) return next;

  const [id, ...rest] = path;

  return (regions ?? []).map((region) =>
    region.id === id
      ? { ...region, regions: writeAt(region.regions ?? [], rest, next) }
      : region
  );
};

/**
 * The first of `base`, `base2`, `base3`... that is not already taken.
 *
 * @param {Array} taken - The names in use.
 * @param {String} base - The name wanted.
 *
 * @returns {String}
 */
export const freeName = (taken, base) => {
  const held = new Set(taken);
  let name = base;
  let suffix = 2;

  while (held.has(name)) {
    name = `${base}${suffix}`;
    suffix += 1;
  }

  return name;
};

/**
 * A region's entries with the list holding a place put through a mapper.
 *
 * The mapper is handed whichever list the place is in - the region's, or a group's - so changing,
 * removing and adding read the same wherever the entry sits.
 *
 * @param {Array} entries - The region's entries.
 * @param {Number|Array} at - The place, as an index or a `[group, child]` pair.
 * @param {Function} mapper - Receives the list the place is in.
 *
 * @returns {Array}
 */
export const alongPath = (entries, at, mapper) =>
  Array.isArray(at)
    ? entries.map((current, index) =>
        index === at[0]
          ? { ...current, entries: mapper(current.entries ?? []) }
          : current
      )
    : mapper(entries);

/**
 * The layout being edited, and everything done to it.
 *
 * The sidebar, the navbar and the page each hold one part of the editor and none of them is a child
 * of the others, so what they share is a session rather than props. Every consumer reads the same
 * regions, and the page draws them as it always does.
 *
 * @returns {Object} The session and the operations on it.
 */
const useLayoutEdit = () => {
  const API = useAPI();
  const [session, setSession] = useAtom(layoutEditAtom);
  const { save: saveLayout, remove: removeLayout } = useLayoutWrites();

  /** What a session holds for one of a section's saved layouts, or for a new one. */
  const sessionFor = (
    sectionName,
    surface,
    defaultLayout,
    parameters,
    layouts,
    row
  ) => ({
    sectionName,
    surface,
    defaultLayout,
    // What the page publishes that a key may index a collection by, named by the page itself: the
    // sidebar is not under the page and cannot read what it publishes.
    parameters,
    layouts,
    layoutId: row?.id ?? null,
    name: row?.name ?? sectionName,
    regions: parseSavedLayout(row) ?? defaultLayout ?? [],
    // The containers opened, outermost first. The page itself is the empty path.
    openPath: [],
    selectedId: '',
    selectedEntry: null,
  });

  const start = useCallback(
    ({ sectionName, surface, defaultLayout = [], parameters = [] }) => {
      // As saved, so a region placed from a preset goes on naming it: what is arranged here is what
      // will be written back.
      API.getSavedSectionLayouts(
        sectionName,
        surface,
        { silent: true },
        (res) => {
          const layouts = res ?? [];

          setSession(
            sessionFor(
              sectionName,
              surface,
              defaultLayout,
              parameters,
              layouts,
              pickSavedLayout(layouts)
            )
          );
        },
        () =>
          setSession(
            sessionFor(
              sectionName,
              surface,
              defaultLayout,
              parameters,
              [],
              null
            )
          )
      );
    },
    []
  );

  const close = useCallback(() => setSession(null), []);

  const change = useCallback(
    (changes) =>
      setSession((current) => (current ? { ...current, ...changes } : current)),
    []
  );

  /** Loads another of the section's layouts, or starts a new one from the checked-in default. */
  const selectLayout = useCallback(
    (id) =>
      setSession((current) => {
        if (!current) return current;

        const row = current.layouts.find((layout) => layout.id === id) ?? null;

        return {
          ...current,
          layoutId: row?.id ?? null,
          name: row?.name ?? current.sectionName,
          regions: parseSavedLayout(row) ?? current.defaultLayout ?? [],
          openPath: [],
          selectedId: '',
          selectedEntry: null,
        };
      }),
    []
  );

  /** Drops the unsaved edits, back to the layout as it was loaded. */
  const discard = useCallback(
    () => selectLayout(session?.layoutId ?? NEW_LAYOUT),
    [session?.layoutId, selectLayout]
  );

  // The page, which is what the canvas draws and arranges whatever is being configured. Opening a
  // container changes what the sidebar is about, not what is on screen: a page that empties itself
  // when a container is opened has taken away the thing being arranged.
  const layout = session?.regions ?? [];

  const setLayout = useCallback((next) => change({ regions: next }), [change]);

  // Where the sidebar is: the containers opened, and the level and container they name.
  const openPath = session?.openPath ?? [];
  const regions = regionsAt(layout, openPath);
  const trail = trailTo(layout, openPath);
  const container = trail[trail.length - 1] ?? null;

  const setRegions = useCallback(
    (next) =>
      setSession((current) =>
        current
          ? {
              ...current,
              regions: writeAt(current.regions, current.openPath ?? [], next),
            }
          : current
      ),
    []
  );

  const selected = regions.find(({ id }) => id === session?.selectedId) ?? null;

  // Choosing a region puts away whichever of its fields was open: what the sidebar shows is one
  // thing at a time, and the field belonged to what was chosen before.
  const select = useCallback(
    (id) => change({ selectedId: id, selectedEntry: null }),
    [change]
  );

  // Choosing one on the page is also leaving whatever container was open, since what was clicked is
  // a region of the page rather than one of its contents.
  const selectOnPage = useCallback(
    (id) => change({ openPath: [], selectedId: id, selectedEntry: null }),
    [change]
  );

  const selectEntry = useCallback(
    (index) => change({ selectedEntry: index }),
    [change]
  );

  // Opening a container goes one level further in rather than replacing where we are, so a container
  // inside a container is reached the way the first one was.
  const open = useCallback(
    (id) =>
      setSession((current) => {
        if (!current) return current;

        return {
          ...current,
          openPath: id ? [...(current.openPath ?? []), id] : [],
          selectedId: '',
          selectedEntry: null,
        };
      }),
    []
  );

  /** Goes back out to a level already opened, for stepping up the path. */
  const openTo = useCallback(
    (path) =>
      change({ openPath: path ?? [], selectedId: '', selectedEntry: null }),
    [change]
  );

  /** Adds a region to a level of the tree, clear of whatever is already placed on it. */
  const addTo = (held, write, widget, placement) => {
    if (!widget) return;

    const id = freeName(
      held.map((region) => region.id),
      widget
    );
    const { x, y, w, h } = { ...NEW_REGION, ...(placement ?? {}) };
    const at = {
      x: Math.min(Math.max(x, 0), DETAIL_COLUMNS - w),
      y: Math.max(y, 0),
      w,
      h,
    };

    write([...held, { id, widget, ...at, y: freeRow(held, at), entries: [] }]);
    change({ selectedId: id, selectedEntry: null });
  };

  /** Drops a widget onto the page, which is the only level the canvas arranges. */
  const addRegion = useCallback(
    (widget, placement = null) => {
      change({ openPath: [] });
      addTo(layout, setLayout, widget, placement);
    },
    [layout, setLayout, change]
  );

  /** Adds a widget inside the container being configured, where placement means nothing. */
  const addChild = useCallback(
    (widget) => addTo(regions, setRegions, widget, null),
    [regions, setRegions]
  );

  const updateRegion = useCallback(
    (changes) =>
      setRegions(
        regions.map((region) =>
          region.id === session?.selectedId ? { ...region, ...changes } : region
        )
      ),
    [regions, session?.selectedId, setRegions]
  );

  const updateEntries = useCallback(
    (mapper) => updateRegion({ entries: mapper(selected?.entries ?? []) }),
    [selected?.entries, updateRegion]
  );

  const removeRegion = useCallback(
    (id) => {
      setRegions(regions.filter((region) => region.id !== id));
      if (id === session?.selectedId) select('');
    },
    [regions, session?.selectedId, setRegions, select]
  );

  /** Takes a region off the page, which is what the canvas removes from whatever is being edited. */
  const removeOnPage = useCallback(
    (id) => {
      setLayout(layout.filter((region) => region.id !== id));
      // Whatever was open inside it is gone with it, so the sidebar comes back out to the page.
      if (openPath.includes(id) || id === session?.selectedId) selectOnPage('');
    },
    [layout, openPath, session?.selectedId, setLayout, selectOnPage]
  );

  const save = () => {
    if (!session) return;

    saveLayout(
      {
        id: session.layoutId,
        sectionName: session.sectionName,
        surface: session.surface,
        name: session.name,
        layout: session.regions,
      },
      () => setSession(null)
    );
  };

  const remove = () => {
    if (!session?.layoutId) return;

    removeLayout(session.layoutId, session.sectionName, () => setSession(null));
  };

  return {
    session,
    layout,
    regions,
    openPath,
    trail,
    container,
    selected,
    start,
    close,
    discard,
    selectLayout,
    setName: (name) => change({ name }),
    setLayout,
    setRegions,
    select,
    selectOnPage,
    selectEntry,
    open,
    openTo,
    addRegion,
    addChild,
    updateRegion,
    updateEntries,
    removeRegion,
    removeOnPage,
    save,
    remove,
  };
};

export default useLayoutEdit;
